import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, Button, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { useI18n } from '../../i18n/i18n';
import {
  analyzeIncidentPatterns,
  getIncidentTrends
} from './incidentsAnalytics';
import { checkForEscalation } from './incidentsEscalation'; // ✅ Правильний імпорт

export default function IncidentsAnalyticsDashboard() {
  const { t } = useI18n();
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [analyticsData, trendsData, escalationsData] = await Promise.all([
        analyzeIncidentPatterns(),
        getIncidentTrends(period),
        checkForEscalation()
      ]);
      
      setAnalytics(analyticsData);
      setTrends(trendsData);
      setEscalations(escalationsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">{t('common.loading') || 'Loading...'}</Typography>
      </Box>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">
          {t('incidents_analytics_title') || 'Incidents Analytics'}
        </Typography>
        
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="7d">7 days</MenuItem>
            <MenuItem value="30d">30 days</MenuItem>
            <MenuItem value="90d">90 days</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Grid container spacing={3}>
        {/* Статистика */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Incidents
              </Typography>
              <Typography variant="h4">
                {analytics.totalIncidents}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Recurring Patterns
              </Typography>
              <Typography variant="h4">
                {analytics.recurringPatterns.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Auto Escalations
              </Typography>
              <Typography variant="h4">
                {escalations.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Frequent Clients
              </Typography>
              <Typography variant="h4">
                {analytics.frequentClients.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Повторювані патерни */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('incidents_recurring_patterns') || 'Recurring Patterns'}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pattern</TableCell>
                    <TableCell>Count</TableCell>
                    <TableCell>Severity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.recurringPatterns.map((pattern, index) => (
                    <TableRow key={index}>
                      <TableCell>{pattern.pattern}</TableCell>
                      <TableCell>{pattern.count}</TableCell>
                      <TableCell>
                        <Chip 
                          label={pattern.severity} 
                          size="small"
                          color={pattern.severity === 'high' ? 'error' : 'warning'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Автоматичні ескалації */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('incidents_auto_escalations') || 'Auto Escalations'}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {escalations.map((escalation, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip 
                          label={escalation.type} 
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>{escalation.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Button 
        variant="outlined" 
        onClick={loadAnalytics}
        sx={{ mt: 2 }}
      >
        Refresh Analytics
      </Button>
    </Box>
  );
}